import { useApolloClient } from '@apollo/client';
import { useEffect } from 'react';

import type { User } from '../graphql/generated';

const LOCAL_STORAGE_USER_KEY = 'hover--current-user';

const setLocalStorageUser = (id: number) =>
  window.localStorage.setItem(LOCAL_STORAGE_USER_KEY, id.toString());

const clearLocalStorageUser = () =>
  window.localStorage.removeItem(LOCAL_STORAGE_USER_KEY);

export interface UseLocalStorageUserOptions {
  /**
   * User record associated with the current session
   */
  user: User | undefined | null;
  /**
   * Callback that refreshes the current session from the server. At the bare
   * minimum, this should refetch the query that provides the `user` record
   * passed to this hook.
   */
  refresh: () => Promise<unknown>;
}

/**
 * A helper hook that attaches `'storage'` event listeners to keep the current
 * user session in sync across browser tabs and windows. This should receive a
 * `user` record representing the user for the current session.
 *
 * @param options
 */
export const useLocalStorageUser = ({
  user,
  refresh,
}: UseLocalStorageUserOptions) => {
  const client = useApolloClient();

  // Clear the user entry from local storage when the Apollo cache is reset. The
  // store is reset when the user explicitly logs out (see `useLogout`).
  useEffect(
    () =>
      client.onResetStore(() =>
        // The `onResetStore` signature expects an asynchronous
        // function even though this operation is synchronous
        Promise.resolve(clearLocalStorageUser()),
      ),
    [client],
  );

  // For the active tab or window, update the local
  // storage entry when the user record changes
  useEffect(() => {
    if (user) {
      setLocalStorageUser(user.id);
    } else {
      clearLocalStorageUser();
    }
  }, [user]);

  const id = user?.id;

  // For background tabs and windows, subscribe to `'storage'` events,
  // which are only dispatched on background tabs, allowing us to
  // respond to the above `useEffect` updates in the active tab.
  useEffect(() => {
    const cb = async ({ key, newValue }: StorageEvent) => {
      if (
        key === LOCAL_STORAGE_USER_KEY &&
        (!newValue || !id || id.toString() !== newValue)
      ) {
        try {
          await client.resetStore();
          await refresh();
        } catch {
          // This catches any authentication errors that might be thrown when
          // invoking the `refresh` callback. For example, refetching the user
          // profile or any other authenticated queries after logout.
        }
      }
    };

    window.addEventListener('storage', cb);

    return () => window.removeEventListener('storage', cb);
  }, [client, id, refresh]);
};
